<template>
  <div class="channelpricerules" v-if="isFeatureEnabled('activitypricing')">
    <p v-if="loading" class="text-muted"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading...') }}</p>
    <table v-else class="table table-striped channelpricerulerows">
      <thead>
        <tr>
          <th>{{ _('Sales channel') }}</th>
          <th v-if="isProductMode">{{ _('General rule') }}</th>
          <th>{{ isProductMode ? _('Rule for this product') : _('Price adjustment') }}</th>
          <th>&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="channel in allChannels" style="cursor: pointer" v-on:click="editRuleDialog(channel)">
          <td>{{ channel.name }}</td>
          <td v-if="isProductMode">{{ formatRule(generalRule(channel)) }}</td>
          <td>{{ formatRule(ownRule(channel)) }}</td>
          <td><a v-if="ownRule(channel)" href="javascript:;" class="pull-right" v-on:click.stop="removeRule(ownRule(channel))"><i class="fa fa-fw fa-close"></i></a></td>
        </tr>
        <tr v-if="!allChannels.length">
          <td :colspan="isProductMode ? 4 : 3"><i>{{ _('No sales channels have been added.') }}</i></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import currencies from '~/lib/currencies'
import errors from '~/lib/errors'
import formats from '~/lib/formats'
import modals from '~/lib/modals'
import settingshelpers from '~/lib/settingshelpers'

// Channel price rules (activity pricing): net price adjustments per sales channel.
// Without productId the general rule of each channel is managed, with productId the product's overrides.
export default {
  data () {
    return {
      loading: false,
      salesChannels: [],
      rules: []
    }
  },
  props: {
    productId: {
      type: Number,
      required: false,
      default: null
    }
  },
  mixins: [currencies, errors, formats, modals, settingshelpers],
  computed: {
    ...mapGetters(['allSettings', 'baseDomain', 'shopId', 'locale', 'version', 'apiKey', 'numberFormat']),
    settings () {
      return this.allSettings[this.shopId + '-' + this.locale] || {}
    },
    isProductMode () {
      return !!this.productId
    },
    // GetYourGuide has a rule of its own with the channel key 'gyg' when the channel is in use
    allChannels () {
      const channels = [...this.salesChannels]
      if (this.isFeatureEnabled('gyg') && parseInt(this.settings.channel_gyg)) {
        channels.push({ id: null, key: 'gyg', name: 'GetYourGuide' })
      }
      return channels
    }
  },
  watch: {
    productId () {
      this.loadRules()
    }
  },
  methods: {
    channelKey (channel) {
      return channel.key || 'saleschannel:' + channel.id
    },
    generalRule (channel) {
      return this.rules.find(r => r.channel_key === this.channelKey(channel) && !parseInt(r.product_id))
    },
    ownRule (channel) {
      if (!this.isProductMode) {
        return this.generalRule(channel)
      }
      return this.rules.find(r => r.channel_key === this.channelKey(channel) && parseInt(r.product_id) === parseInt(this.productId))
    },
    formatRule (rule) {
      if (!rule) {
        return '–'
      }
      const value = parseFloat(rule.value) || 0
      const formatted = (value > 0 ? '+' : '') + this.formatNumber(value, this.numberFormat, Number.isInteger(value) ? 0 : 2)
      if (rule.adjust_type === 'fixed') {
        return formatted + ' ' + this.currencySymbol(this.settings.currency)
      }
      return formatted + ' %'
    },
    editRuleDialog (channel) {
      const rule = this.ownRule(channel)
      const buttons = [
        {
          title: this._('Close'),
          icon: 'close',
          handler: () => {}
        },
        {
          title: this._('Ok'),
          class: 'success',
          icon: 'check',
          handler: (data) => {
            this.saveRule(channel, rule, data)
          }
        }
      ]
      if (rule) {
        buttons.unshift({
          title: this._('Delete'),
          class: 'danger',
          icon: 'trash',
          handler: () => {
            this.removeRule(rule)
          }
        })
      }
      this.showDialog({
        title: channel.name,
        buttons,
        fields: [
          {
            title: this._('Adjustment type'),
            type: 'select',
            name: 'adjust_type',
            options: [
              { value: 'percent', name: this._('Percentage') },
              { value: 'fixed', name: this._('Fixed amount') }
            ],
            defaultValue: rule ? rule.adjust_type : 'percent'
          },
          {
            title: this._('Adjustment'),
            type: 'text',
            name: 'value',
            placeholder: this._('For example -20'),
            defaultValue: rule ? rule.value : '',
            help: this._('A negative value lowers the price and a positive value raises it. Leave empty to remove the rule.')
          }
        ]
      })
    },
    async saveRule (channel, rule, data) {
      const value = data && data.value !== undefined && data.value !== null ? String(data.value).trim().replace(',', '.') : ''
      if (value === '') {
        if (rule) {
          this.removeRule(rule)
        }
        return
      }
      const ruleData = {
        adjust_type: data.adjust_type || 'percent',
        value
      }
      if (channel.key) {
        ruleData.channel_key = channel.key
      } else {
        ruleData.saleschannel_id = channel.id
      }
      if (this.isProductMode) {
        ruleData.product_id = this.productId
      }
      try {
        if (rule) {
          await axios.put(this.baseDomain + this.shopId + '/channelpricerules/' + rule.id + '.json?apikey=' + this.apiKey, ruleData)
        } else {
          await axios.post(this.baseDomain + this.shopId + '/channelpricerules.json?apikey=' + this.apiKey, ruleData)
        }
      } catch (e) {
        this.showError(e)
        return
      }
      this.showNotification(this._('Changes saved successfully!'))
      this.loadRules()
    },
    async removeRule (rule) {
      try {
        await axios.delete(this.baseDomain + this.shopId + '/channelpricerules/' + rule.id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        this.showError(e)
        return
      }
      this.rules = this.rules.filter(r => r.id !== rule.id)
    },
    async loadSalesChannels () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/settings/1/saleschannels.json?apikey=' + this.apiKey + '&version=' + this.version)
      } catch (e) {
        this.showError(this._('Error loading sales channels! Please try again.'))
        return
      }
      const rows = response.data || []
      this.salesChannels = Array.isArray(rows) ? rows : Object.values(rows)
    },
    async loadRules () {
      let response
      try {
        // Product overrides are shown next to the general rules, so load all rules
        response = await axios.get(this.baseDomain + this.shopId + '/channelpricerules.json?apikey=' + this.apiKey)
      } catch (e) {
        this.showError(e)
        return
      }
      const rows = response.data || []
      this.rules = Array.isArray(rows) ? rows : Object.values(rows)
    }
  },
  async created () {
    if (!this.isFeatureEnabled('activitypricing')) {
      return
    }
    this.loading = true
    await Promise.all([this.loadSalesChannels(), this.loadRules()])
    this.loading = false
  }
}
</script>
